import type { TExample } from '@atlas/uicorn/preview/modules';
import { useMemo, useState } from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';
import { Form as AntForm, type FormProps as TAntFormProps, type FormInstance as TAntFormInstance } from 'antd';
import FormItem, { SFormItem } from '@atlas/uicorn/lib/Control/FormItem';
import Input, { SInput, SInputMultiline, SMultilineInputContainer } from '@atlas/uicorn/lib/Input/Input';
import Button from '@atlas/uicorn/lib/Control/Button';
import { SFormItemRow } from '@atlas/uicorn/lib/Control/FormItemRow';
import { SSelect } from '@atlas/uicorn/lib/Input/Select';
import useOnChange from '@atlas/tool/box/hooks/useOnChange';

const Form = React.memo(
    React.forwardRef<TFormRef, TFormProps>(function Form(props, ref): React.ReactElement {
        const { inputColumn = defaultInputColumnBasis, focusFirstInput, ...formProps } = props;
        const context = useMemo<TFormContext>(() => ({ disabled: !!props.disabled }), [props.disabled]);

        const [outerRef, setOuterRef] = useState<HTMLDivElement | null>(null);

        useOnChange(
            ([prev]) => {
                if (!focusFirstInput || prev) return;

                const allInputs = outerRef?.querySelectorAll('.ant-input');

                if (!allInputs?.length) return;

                let firstValidElement;

                for (const input of Array.from(allInputs)) {
                    if (input.closest('.ant-form-item-hidden')) continue;
                    if (input.getAttribute('data-disabled') === 'true') continue;

                    firstValidElement = input as HTMLInputElement;
                    break;
                }

                if (!firstValidElement) return;

                firstValidElement.focus();
            },
            [outerRef, focusFirstInput],
        );

        return (
            <FormProvider value={context}>
                <div ref={setOuterRef}>
                    <SForm
                        {...formProps}
                        layout={props.layout === 'page' ? 'vertical' : props.layout ?? 'horizontal'}
                        validateTrigger={validateTrigger}
                        requiredMark={false}
                        $basis={inputColumn}
                        $pageStyles={props.layout === 'page'}
                        ref={ref}
                    />
                </div>
            </FormProvider>
        );
    }),
);

function FormRuler() {
    return <SFormRuler />;
}

export default Object.assign(Form, {
    useForm: AntForm.useForm,
    useFormInstance: AntForm.useFormInstance,
    useWatch: AntForm.useWatch,
    Ruler: FormRuler,
});

// #region Helpers

const defaultInputColumnBasis: TFormInputColumn = '50%';

export const FormContext = React.createContext<TFormContext>({ disabled: false });

export const FormProvider = FormContext.Provider;

const validateTrigger = ['onChange', 'onSubmit'];

// #endregion

// #region Styles

const SFormRuler = styled.hr`
    margin: 0;
    border: none;
    border-top: 0.1rem solid ${(p) => p.theme.color.grey61};
`;

const SForm = styled(AntForm)<{ $basis: string; $pageStyles?: boolean }>`
    --form-gap: 1.6rem;

    &.ant-form-vertical > :is(${SFormItem}, ${SFormItemRow}, ${SFormRuler}):not(:first-child) {
        margin-top: var(--form-gap);
    }

    &.ant-form-horizontal {
        .atlas-form-item-vertical {
            .ant-form-item-row {
                flex-direction: column;
            }

            .ant-form-item-label {
                padding: 0 0 8px;
                line-height: 1.5715;
                white-space: initial;
                text-align: left;

                > label {
                    height: auto;
                    margin: 0;
                }
            }

            .ant-form-item-control {
                width: 100%;
                flex: 1 0 auto;
            }
        }

        .ant-form-item:where(:not(.atlas-form-item-vertical)) {
            .ant-form-item-row {
                align-items: flex-start;
                gap: var(--form-gap);
                flex-wrap: nowrap;
            }

            .ant-form-item-label {
                flex-grow: 1;
                text-align: left;
            }

            .ant-form-item-label > label {
                min-height: 3.5rem;
                height: auto;
                display: inline-flex;
                align-items: center;
            }

            .ant-form-item-control {
                flex: unset;
                flex-basis: ${(p) => p.$basis};
                flex-shrink: 0;
            }
        }

        .atlas-form-item-shrink {
            .ant-form-item-control {
                flex-basis: auto;
                flex-shrink: 0;
                max-width: ${defaultInputColumnBasis};
            }
        }
    }

    .ant-form-item-margin-offset {
        display: none;
    }

    ${(p) =>
        p.theme.when.screenVariant(
            'tiny',
            css`
                &.ant-form-horizontal .ant-form-item:where(:not(.atlas-form-item-vertical)) .ant-form-item-row {
                    flex-direction: column;
                    gap: 0;

                    > * {
                        width: 100%;
                    }
                }
            `,
        )}

    ${(p) =>
        p.$pageStyles
            ? css`
                  --form-page-input-offset: 3.2rem;

                  ${SFormItem} {
                      & > .ant-form-item-row > .ant-form-item-control > div > .ant-form-item-explain {
                          padding: 0.4rem 1.1rem 0.4rem var(--form-page-input-offset);
                      }

                      ${SSelect} .ant-select-selection-item {
                          background: ${(p) => p.theme.color.grey6};
                          border-radius: 0.5rem;

                          padding: 0.2rem 0.6rem 0.2rem 0.8rem;
                          height: 2.7rem;
                          text-transform: capitalize;

                          .ant-select-selection-item-remove {
                              font-weight: bold;
                              svg {
                                  stroke-width: 4em;
                                  font-size: 1.4rem;
                                  stroke: ${(p) => p.theme.color.grey8};
                                  color: ${(p) => p.theme.color.grey8};
                              }
                          }
                      }

                      ${SSelect} .ant-select-selection-item__error {
                          color: ${(p) => p.theme.color.red6};
                      }

                      ${SMultilineInputContainer} {
                          &,
                          &:has(.ant-input:is(:not(:disabled):not([data-disabled='true'])):focus),
                          &:has(.ant-input:is(:not(:disabled):not([data-disabled='true']))[data-focus='true']) {
                              background: none;
                              border: none;
                              box-shadow: none;
                              padding: 0;
                          }
                      }

                      ${SInput}, ${SInputMultiline} {
                          border-radius: 0;

                          &:is(:not(:disabled):not([data-disabled='true'])):focus {
                              border: none;
                              box-shadow: inset 0.3rem 0 0.1rem -0.1rem ${(p) => p.theme.color.grey61};
                          }

                          &,
                          &.ant-input-status-error:not(.ant-input-disabled):not(.ant-input-borderless).ant-input {
                              background: transparent;
                          }
                      }

                      ${SInput}, ${SInputMultiline},
                      ${SSelect} .ant-select-selector,
                      [data-ui-role='rich-input'] {
                          padding-left: var(--form-page-input-offset);
                          padding-right: 0.8rem;
                      }

                      [data-ui-role='rich-input-editable'] {
                          padding-left: var(--form-page-input-offset);
                          margin-left: calc(-1 * var(--form-page-input-offset));
                      }

                      [data-ui-role='rich-input'] [contenteditable='true']:focus,
                      :where(${SInput}, ${SInputMultiline}):focus,
                      ${SSelect}.ant-select-focused:not(.ant-select-disabled).ant-select:not(
                              .ant-select-customize-input
                          )
                          .ant-select-selector {
                          box-shadow: inset 0.3rem 0 0.1rem -0.1rem ${(p) => p.theme.color.grey61};
                      }

                      [data-ui-role='rich-input'] [contenteditable='false']:not(.atlas-block),
                      :where(${SInput}, ${SInputMultiline}):disabled,
                      ${SSelect}.ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector {
                          opacity: 1;
                          background: transparent;
                          cursor: text;
                          color: ${(p) => p.theme.color.grey9};
                      }

                      [data-ui-role='rich-input'] {
                          opacity: 1;
                      }

                      ${SSelect}.ant-select-disabled.ant-select:not(.ant-select-customize-input)
    .ant-select-selector
    .ant-select-selection-search {
                          display: none;
                      }

                      ${SSelect}.ant-select-disabled.ant-select:not(.ant-select-customize-input)
    .ant-select-selector
    .ant-select-selection-item {
                          color: ${(p) => p.theme.color.grey9};
                          background: ${(p) => p.theme.color.grey5};
                          cursor: default;
                      }

                      &.ant-form-item-has-error :not(.ant-input-disabled):not(.ant-input-borderless).ant-input:focus,
                      &.ant-form-item-has-error
                          :not(.ant-input-affix-wrapper-disabled):not(
                              .ant-input-affix-wrapper-borderless
                          ).ant-input-affix-wrapper:focus,
                      &.ant-form-item-has-error :not(.ant-input-disabled):not(.ant-input-borderless).ant-input-focused,
                      &.ant-form-item-has-error
                          :not(.ant-input-affix-wrapper-disabled):not(
                              .ant-input-affix-wrapper-borderless
                          ).ant-input-affix-wrapper-focused {
                          box-shadow: inset 0.3rem 0 0.1rem -0.1rem rgb(255 77 79 / 20%);
                      }

                      ${SSelect} .ant-select-selector {
                          .ant-select-selection-search {
                              margin-inline-start: 0;
                          }

                          .ant-select-selection-placeholder {
                              left: var(--form-page-input-offset);
                          }
                      }

                      [data-ui-role='rich-input'] {
                          overflow: visible;

                          &::after {
                              content: '';
                              clear: both;
                              height: 0;
                              display: block;
                          }
                      }
                  }
              `
            : ''}
`;

// #endregion

// #region Types

export type TFormProps = Pick<
    TAntFormProps,
    | 'name'
    | 'className'
    | 'form'
    | 'disabled'
    | 'initialValues'
    | 'preserve'
    | 'onFieldsChange'
    | 'onFinish'
    | 'onFinishFailed'
    | 'onValuesChange'
    | 'onKeyDown'
> & {
    inputColumn?: TFormInputColumn;
    layout?: TAntFormProps['layout'] | 'page';
    focusFirstInput?: boolean;
    children?: React.ReactNode;
};

export type TFormInputColumn = `${number}${'px' | '%' | 'rem'}`;

export type TFormRef<Values = any> = TAntFormInstance<Values>;

export type TFormContext = {
    disabled: boolean;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <Form
                layout={props.layout}
                disabled={props.disabled}
                onFieldsChange={props.onFieldsChange}
                onFinish={props.onFinish}
                onFinishFailed={props.onFinishFailed}
                onValuesChange={props.onValuesChange}
            >
                <FormItem name="field1" label="Field 1" help="Hint">
                    <Input />
                </FormItem>
                <FormItem name="field2" description="Note" label="Field 2">
                    <Input />
                </FormItem>
                <FormItem>
                    <Button submit children="Submit" />
                </FormItem>
            </Form>
        );
    },
    controls: {
        layout: ['horizontal', 'vertical', 'inline'],
        disabled: 'boolean',
    },
    handlers: {
        onFieldsChange: 'console',
        onFinish: 'console',
        onFinishFailed: 'console',
        onValuesChange: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?type=design&node-id=6-891&mode=design&t=dygdJrYUTAzsGhAY-0',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Form', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
